import type { ReactNode } from 'react';
import {
  Gift,
  Heart,
  Link2,
  Repeat2,
  UserPlus,
  Wallet,
} from 'lucide-react';

import { DiscordIcon, TelegramIcon, XLogoIcon } from '@/components/icons/BrandIcons';
import { IconBadge, type IconBadgeTone } from '@/components/ui/IconBadge';
import { cn } from '@/lib/utils';
import type { Quest, QuestCategory, QuestType } from '@/lib/types';

import { QuestIcon } from './QuestIcon';

/**
 * Visual variant rendered as the modal hero (16:9 banner). Each quest type
 * carries its own brand color + glyph so visitors recognize the platform at
 * a glance — X is dark, Telegram is sky-blue, Discord is indigo, etc. Falls
 * back to a category-mapped BigWater gradient when the type has no dedicated
 * art.
 */
interface HeroVariant {
  /** Background gradient classes — applied to the outer 16:9 container. */
  gradient: string;
  /** Large background art (offset, low opacity) — the "wallpaper". */
  glyph: ReactNode;
  /** Optional decorative overlay (orbs, dots, waves). */
  pattern?: ReactNode;
  /** Tone for the bottom-left IconBadge — keeps continuity with the cards. */
  badgeTone: IconBadgeTone;
  /**
   * Optional override for the bottom-left IconBadge content. Used by the X
   * family — every X quest shares the same big X wallpaper, so the badge is
   * what differentiates Follow / Like / Retweet.
   */
  badgeIcon?: ReactNode;
}

/** Soft white orbs — used as a default backdrop on most variants. */
export function Orbs() {
  return (
    <div className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay">
      <div className="absolute -left-10 -top-10 h-48 w-48 rounded-full bg-white/30 blur-3xl" />
      <div className="absolute -bottom-12 right-0 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
    </div>
  );
}

/** Sparse dot grid — for crypto / wallet feel. */
function DotGrid() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.18]"
    >
      <defs>
        <pattern id="hero-dots" width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.2" fill="white" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hero-dots)" />
    </svg>
  );
}

/**
 * Calm, low-frequency swells — Visit / BigWater "open ocean" feel.
 * Two long Bézier curves, slow rise-and-fall.
 */
export function WaveLayers() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 200"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 w-full"
    >
      <path
        d="M0,140 C120,180 220,90 320,120 C420,150 520,90 600,110 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.10)"
      />
      <path
        d="M0,160 C100,200 240,120 360,150 C460,175 540,130 600,150 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.07)"
      />
    </svg>
  );
}

/**
 * Higher-frequency, three-layer wave train — Telegram "in motion" energy.
 * Tighter peaks via repeated T-commands give it a livelier rhythm than the
 * Visit variant.
 */
function TelegramWaves() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 200"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 w-full"
    >
      <path
        d="M0,110 Q75,55 150,100 T300,100 T450,100 T600,100 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.10)"
      />
      <path
        d="M0,140 Q75,95 150,130 T300,130 T450,130 T600,130 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.08)"
      />
      <path
        d="M0,170 Q75,145 150,160 T300,160 T450,160 T600,160 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.06)"
      />
    </svg>
  );
}

/**
 * Bold, sculptural swell — Discord "wide signal" feel. Single dramatic
 * Bézier per layer (fewer peaks, stronger curvature) so it reads as a
 * different wave family than Telegram.
 */
function DiscordWaves() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 200"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 w-full"
    >
      <path
        d="M0,90 C200,200 400,20 600,110 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.10)"
      />
      <path
        d="M0,140 C150,210 380,80 600,150 L600,200 L0,200 Z"
        fill="rgba(255,255,255,0.07)"
      />
    </svg>
  );
}

/** Centered, large background glyph — the "big mark" of each variant. */
export function BackgroundGlyph({ children }: { children: ReactNode }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 right-2 flex items-center justify-end pr-4 sm:pr-8"
    >
      <span className="text-white/15 [&>svg]:h-44 [&>svg]:w-44 sm:[&>svg]:h-56 sm:[&>svg]:w-56">
        {children}
      </span>
    </div>
  );
}

// ─── Variants ──────────────────────────────────────────────────────────────

function visitVariant(): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-water-500 via-water-600 to-water-800',
    glyph: (
      <BackgroundGlyph>
        {/* Brand logomark as a white silhouette at the same 15% as the lucide glyphs. */}
        <img
          src="/logomark.svg"
          alt=""
          className="h-44 w-44 opacity-15 brightness-0 invert sm:h-56 sm:w-56"
        />
      </BackgroundGlyph>
    ),
    pattern: (
      <>
        <Orbs />
        <WaveLayers />
      </>
    ),
    badgeTone: 'water',
  };
}

/**
 * Shared X family — same dark gradient + big X wallpaper for all three quest
 * types. The bottom-left badge icon is what differentiates them.
 */
function xVariant(badgeIcon: ReactNode): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-ink-900 via-ink-950 to-black',
    glyph: (
      <BackgroundGlyph>
        <XLogoIcon />
      </BackgroundGlyph>
    ),
    pattern: <DotGrid />,
    badgeTone: 'water',
    badgeIcon,
  };
}

function telegramVariant(): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-[#37AEE2] via-[#1E88E5] to-[#0F4C81]',
    glyph: (
      <BackgroundGlyph>
        <TelegramIcon />
      </BackgroundGlyph>
    ),
    pattern: (
      <>
        <Orbs />
        <TelegramWaves />
      </>
    ),
    badgeTone: 'water',
  };
}

function discordVariant(): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-[#7C8CFF] via-[#5865F2] to-[#3A41B0]',
    glyph: (
      <BackgroundGlyph>
        <DiscordIcon />
      </BackgroundGlyph>
    ),
    pattern: (
      <>
        <Orbs />
        <DiscordWaves />
      </>
    ),
    badgeTone: 'water',
  };
}

function walletVariant(): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-amber2-500 via-amber2-600 to-ink-900',
    glyph: (
      <BackgroundGlyph>
        <Wallet strokeWidth={1.25} />
      </BackgroundGlyph>
    ),
    pattern: <DotGrid />,
    badgeTone: 'amber',
  };
}

function bridgeVariant(): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-cyan2-400 via-cyan2-500 to-water-700',
    glyph: (
      <BackgroundGlyph>
        <Link2 strokeWidth={1.5} />
      </BackgroundGlyph>
    ),
    pattern: <Orbs />,
    badgeTone: 'cyan',
  };
}

function referralVariant(): HeroVariant {
  return {
    gradient: 'bg-gradient-to-br from-rose2-400 via-rose2-500 to-water-700',
    glyph: (
      <BackgroundGlyph>
        <Gift strokeWidth={1.25} />
      </BackgroundGlyph>
    ),
    pattern: <Orbs />,
    badgeTone: 'rose',
  };
}

/** Map an X-family quest to its differentiating badge icon. */
function xBadgeIconFor(quest: Quest): ReactNode {
  if (quest.type === 'twitter_follow') {
    return <UserPlus size={28} strokeWidth={1.75} aria-hidden="true" />;
  }
  if (quest.icon === 'repeat-2') {
    return <Repeat2 size={28} strokeWidth={1.75} aria-hidden="true" />;
  }
  // twitter_post_engage / twitter_thread that isn't a retweet → like.
  return <Heart size={28} strokeWidth={1.75} aria-hidden="true" />;
}

/** Category-mapped fallback — same palette as before, no orphaned look. */
const CATEGORY_FALLBACK: Record<QuestCategory, string> = {
  onboarding: 'bg-gradient-to-br from-water-500 via-water-600 to-water-800',
  social: 'bg-gradient-to-br from-rose2-500 via-rose2-600 to-water-700',
  web3: 'bg-gradient-to-br from-cyan2-500 via-water-600 to-water-800',
  bonus: 'bg-gradient-to-br from-amber2-500 via-amber2-600 to-water-800',
};

const CATEGORY_TONE: Record<QuestCategory, IconBadgeTone> = {
  onboarding: 'water',
  social: 'rose',
  web3: 'cyan',
  bonus: 'amber',
};

function fallbackVariant(quest: Quest): HeroVariant {
  return {
    gradient: CATEGORY_FALLBACK[quest.category],
    glyph: <></>,
    pattern: <Orbs />,
    badgeTone: CATEGORY_TONE[quest.category],
  };
}

/** Map the quest type to its hero variant. */
function pickVariant(quest: Quest): HeroVariant {
  const t: QuestType = quest.type;
  if (t === 'visit') {
    // A plain link to X ("Follow us on X") shares the X family banner.
    return quest.icon === 'x'
      ? xVariant(<UserPlus size={28} strokeWidth={1.75} aria-hidden="true" />)
      : visitVariant();
  }
  if (t === 'twitter_follow' || t === 'twitter_post_engage' || t === 'twitter_thread') {
    return xVariant(xBadgeIconFor(quest));
  }
  if (t === 'telegram_join') return telegramVariant();
  if (t === 'discord_join' || t === 'discord_role') return discordVariant();
  if (t === 'wallet_hold') return walletVariant();
  if (t === 'bridge') return bridgeVariant();
  if (t === 'referral') return referralVariant();
  return fallbackVariant(quest);
}

interface QuestHeroProps {
  quest: Quest;
}

/**
 * Hero banner shown at the top of the QuestModal. Type-specific gradient +
 * branded glyph so each quest reads as its own platform without needing
 * uploaded artwork. The bottom-left IconBadge keeps continuity with the
 * cards in the section grid.
 */
export function QuestHero({ quest }: QuestHeroProps) {
  const variant = pickVariant(quest);
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative flex aspect-[16/9] w-full items-end overflow-hidden p-6',
        variant.gradient,
      )}
    >
      {variant.pattern}
      {variant.glyph}
      <IconBadge
        size="lg"
        tone={variant.badgeTone}
        className="relative z-10 bg-white/95 ring-white/40 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.4)]"
      >
        {variant.badgeIcon ?? <QuestIcon name={quest.icon} size={28} strokeWidth={1.75} />}
      </IconBadge>
    </div>
  );
}

export default QuestHero;
